import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/context';
import { getSupabase } from '@/lib/supabase';
import { safeSessionStorage } from '@/lib/storage';
import { PreviewContext, previewAccess, type PreviewValue } from './preview';
import { hasPermission, isRoleCode, type RoleCode } from './types';

const PREVIEW_KEY = 'aqs.preview.role';

interface Loaded {
  role: RoleCode;
  permissions: string[];
  portalPath: string | null;
}

async function loadRole(role: RoleCode): Promise<Loaded | null> {
  const supabase = await getSupabase();
  if (!supabase) return null;
  const [perms, row] = await Promise.all([
    supabase.rpc('role_permission_set', { p_role: role }),
    supabase.from('roles').select('portal_path').eq('code', role).maybeSingle(),
  ]);
  if (perms.error || row.error) return null;
  const permissions = Array.isArray(perms.data) ? (perms.data as unknown[]).filter((p): p is string => typeof p === 'string') : [];
  const portalPath = (row.data as { portal_path?: unknown } | null)?.portal_path;
  return { role, permissions, portalPath: typeof portalPath === 'string' ? portalPath : null };
}

/**
 * Holds the administrator's portal preview (see preview.ts). The chosen role
 * survives reloads in this tab only (sessionStorage) and is dropped as soon as
 * the signed-in user no longer holds portal.preview.
 */
export function PreviewProvider({ children }: { children: ReactNode }) {
  const { access } = useAuth();
  const canPreview = hasPermission(access, 'portal.preview');
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!canPreview) {
      setLoaded(null);
      return;
    }
    const saved = safeSessionStorage.get(PREVIEW_KEY);
    if (!isRoleCode(saved)) return;
    let active = true;
    void loadRole(saved).then((result) => {
      if (active) setLoaded(result);
    });
    return () => {
      active = false;
    };
  }, [canPreview]);

  const start = useCallback(
    async (role: RoleCode) => {
      if (!canPreview) return false;
      const result = await loadRole(role);
      if (!result) return false;
      safeSessionStorage.set(PREVIEW_KEY, role);
      setLoaded(result);
      const supabase = await getSupabase();
      // Recorded in the administrator's account activity; a failure never blocks the preview.
      void supabase?.rpc('log_portal_preview', { p_role: role }).then(() => undefined, () => undefined);
      return true;
    },
    [canPreview],
  );

  const stop = useCallback(() => {
    safeSessionStorage.remove(PREVIEW_KEY);
    setLoaded(null);
  }, []);

  const value = useMemo<PreviewValue>(() => {
    const active = !!(access && canPreview && loaded);
    return {
      role: active ? loaded!.role : null,
      active,
      access: active ? previewAccess(access!, loaded!.role, loaded!.permissions, loaded!.portalPath) : access,
      realAccess: access,
      canPreview,
      start,
      stop,
    };
  }, [access, canPreview, loaded, start, stop]);

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}
